import { BaseEntity } from '../base-entity';
import { Bookmark } from './bookmark';
import { Dataset } from './dataset';
import { IdentityPrincipal } from './identity-principal';
import { OrganisationWorkspace } from './organisation-workspace';
import { PbiReport } from './pbi-report';
import { ReportAccess } from './report-access';
import { ReportActivityRecord } from './report-activity-record';
import { ReportUserPreference } from './report-user-preference';
import { Role } from './role';

/// <code-import> Place custom imports between <code-import> tags
import { EntityName } from '@mri-platform/dsg/core';
import { AuthzContext, AuthzContextsActionMap, CrudAction, CrudAllAction } from '@mri-platform/resource-authz';
import { distinctValues } from '@mri-platform/shared/core';
import { breeze, MetadataStore } from 'breeze-client';
/// </code-import>

export interface Report {
  id: string;
  category?: string;
  createdDate: Date;
  datasetId: string;
  description?: string;
  embedUrl: string;
  hidesReportId?: string;
  isCustom: boolean;
  isDefault: boolean;
  isDeleted: boolean;
  isEmbedOnly: boolean;
  isPublished: boolean;
  lastUpdatedBy: string;
  lastUpdatedDate: Date;
  latestProblem?: string;
  minOverrideEmbedOnlyRoleId?: string;
  name: string;
  organisationWorkspaceId: string;
  ownerId: string;
  parentReportId?: string;
  periodEnd: Date;
  periodStart: Date;
  templateReportName?: string;
  tenantKey: string;
  version: number;
  webUrl: string;
  activityRecords: ReportActivityRecord[];
  allowedPrincipals: ReportAccess[];
  bookmarks: Bookmark[];
  dataset: Dataset;
  hidesReport: Report;
  minOverrideEmbedOnlyRole: Role;
  organisationWorkspace: OrganisationWorkspace;
  owner: IdentityPrincipal;
  parentReport: Report;
  pbiReports: PbiReport[];
  userPreferences: ReportUserPreference[];
}

/// <module-code> Place module level code between <module-code> tags
export enum ReportCustomAction {
  Export = 'Export'
}

export const DeleteCustomReportTag = 'UNSAFE_DELETE_ORPHANED_CUSTOM_REPORT';

export enum ReportCategory {
  AskAgora = 'AskAgora'
}

/**
 * This function removes the prefix from the given string.
 * The prefix is a number followed by a tilde (e.g., "1\~", "2\~").
 */
export const removeCategoryPrefix = (str: string): string => str.replace(/^\d+~/, '');

const typeFuncs = BaseEntity.makeStaticTypeFunctions<Report>(EntityName.Report);
/// </module-code>

export class Report extends BaseEntity {
  /// <code> Place custom code between <code> tags
  static propName = typeFuncs.propName;
  static propNames = typeFuncs.propNames;
  static qualifiedPropName = typeFuncs.qualifiedPropName;
  static qualifiedPropNames = typeFuncs.qualifiedPropNames;

  static authorization: AuthzContextsActionMap = {
    ...AuthzContextsActionMap.crudFor(EntityName.Report),
    [ReportCustomAction.Export]: {
      action: ReportCustomAction.Export,
      resource: EntityName.Report
    },
    /* Note: The Report will always have a PbiReport on the server-side,
    hence if a report is deleted than corresponding pbiReports get deleted too.*/
    [CrudAction.Delete]: AuthzContext.updateFor(
      Report.qualifiedPropName('isDeleted'),
      PbiReport.qualifiedPropName('isDeleted')
    ),
    [CrudAllAction.Delete]: AuthzContext.updateAllFor(
      Report.qualifiedPropName('isDeleted'),
      PbiReport.qualifiedPropName('isDeleted')
    ),
    [CrudAllAction.Read]: AuthzContext.readAllFor(EntityName.Report)
  };

  static propPath<K1 extends keyof Report, K2 extends keyof Report[K1]>(k1: K1, k2: K2): string;
  static propPath<K1 extends keyof Report, K2 extends keyof Report[K1], K3 extends keyof Report[K1][K2]>(
    k1: K1,
    k2: K2,
    k3: K3
  ): string;
  static propPath(...keys: string[]) {
    return super.getPropPath(keys);
  }

  static register(metadataStore: MetadataStore) {
    const et = metadataStore.getAsEntityType(EntityName.Report, false);
    //NOTE:Ensure the Regex is maintained in sync with the server-side, which can be found in Report.cs file.
    // see link https://github.com/MRI-Software/data-services-gateway/blob/master/src/DataServicesGateway.Shared/Model/Report.cs for more information
    const validNameRegEx = breeze.Validator.makeRegExpValidator(
      'validName',
      /^(?!.*[[\]]).*$/,
      "'%displayName%' cannot contain '[' or ']'"
    );
    //Register the validator with breeze
    breeze.Validator.register(validNameRegEx);

    const prop = et.getProperty(Report.propName('name'));
    prop.validators.push(validNameRegEx);
  }

  static hasMultipleWorkspaces = (reports: Report[]) => distinctValues(reports, 'organisationWorkspaceId').length > 1;
  /// </code>
  // Generated code. Do not place code below this line.
}
